import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, MessageSquare, X, ChevronDown, ChevronUp, Send } from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

const TYPE_LABELS = { issue:'🔧 Issue / Problem', material_request:'📦 Material Request', clarification:'❓ Need Clarification', other:'💬 Other' };
const STATUS_COLORS = { open:'bg-red-100 text-red-700', in_review:'bg-yellow-100 text-yellow-700', resolved:'bg-green-100 text-green-700', closed:'bg-gray-100 text-gray-600' };

export default function WorkerQueriesPage() {
  const [queries, setQueries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [followUpId, setFollowUpId] = useState(null);
  const [followUpText, setFollowUpText] = useState('');
  const [sendingFollowUp, setSendingFollowUp] = useState(false);

  const handleFollowUp = async (q) => {
    if (!followUpText.trim()) return toast.error('Please write something');
    setSendingFollowUp(true);
    try {
      // Append follow-up to description by raising a new linked query
      await api.post('/queries', {
        task_id: q.task_id,
        project_id: q.project_id,
        project_item_id: q.project_item_id,
        department_id: q.department_id,
        query_type: q.query_type,
        title: `[Follow-up] ${q.title}`,
        description: followUpText,
        priority: q.priority
      });
      toast.success('Follow-up sent! ✅');
      setFollowUpId(null);
      setFollowUpText('');
      load();
    } catch(e) { toast.error('Error'); }
    finally { setSendingFollowUp(false); }
  };

  const handleFilesChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setForm(f => ({ ...f, images: selected }));
  };
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    task_id: '', project_id: '', project_item_id: '', department_id: '',
    query_type: 'issue', title: '', description: '', priority: 'medium',
    images: []
  });
  const [items, setItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [qRes, pRes, tRes, dRes] = await Promise.all([
        api.get('/queries', { params: { my: true } }),
        api.get('/projects'),
        api.get('/tasks/my', { params: { status: 'all' } }),
        api.get('/departments', { params: { active_only: '1' } })
      ]);
      setQueries(qRes.data);
      setProjects(pRes.data);
      setMyTasks(tRes.data);
      setDepartments(dRes.data);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}`).then(r => setItems(r.data.items || [])).catch(()=>{});
    }
  }, [form.project_id]);

  // Auto-fill from task
  useEffect(() => {
    if (form.task_id) {
      const t = myTasks.find(x => x.id == form.task_id);
      if (t) setForm(f => ({
        ...f, project_id: t.project_id, department_id: t.department_id || ''
      }));
    }
  }, [form.task_id]);

  const handleSubmit = async () => {
    if (!form.project_id || !form.title || !form.description) {
      return toast.error('Project, Title and Description are required');
    }
    try {
      const payload = {
        task_id: form.task_id || null,
        project_id: form.project_id,
        project_item_id: form.project_item_id || null,
        department_id: form.department_id || null,
        query_type: form.query_type,
        title: form.title,
        description: form.description,
        priority: form.priority
      };
      const response = await api.post('/queries', payload);
      const queryId = response.data.id;

      if (form.images?.length > 0) {
        const data = new FormData();
        form.images.forEach(file => data.append('images', file));
        await api.post(`/queries/${queryId}/images`, data);
      }

      toast.success('Query submitted! Admin will review it 👍');
      setShowForm(false);
      setForm({ task_id:'', project_id:'', project_item_id:'', department_id:'',
        query_type:'issue', title:'', description:'', priority:'medium', images: [] });
      load();
    } catch (err) {
      console.error('Query submit error:', err);
      toast.error('Error submitting query');
    }
  };

  const openCount = queries.filter(q => q.status === 'open').length;
  const resolvedCount = queries.filter(q => q.status === 'resolved').length;

  const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    const now = new Date();
    const diff = now - d;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return Math.floor(diff/60000) + ' min ago';
    if (hrs < 24) return hrs + ' hours ago';
    return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short' });
  };

  return (
    <div className="p-4 max-w-2xl mx-auto pb-20">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Queries</h1>
          <p className="text-gray-500 text-xs mt-0.5">Send issues, material requests or questions here</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Raise Query
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{openCount}</p>
          <p className="text-xs text-red-500 mt-0.5">Open (Pending)</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{resolvedCount}</p>
          <p className="text-xs text-green-500 mt-0.5">Resolved</p>
        </div>
      </div>

      {/* Query List */}
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : queries.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">No queries yet — everything is fine! 😊</p>
          <button onClick={() => setShowForm(true)} className="mt-3 text-amber-600 text-sm font-medium hover:underline">
            Raise your first query
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {queries.map(q => {
            const isOpen = expandedId === q.id;
            return (
              <div key={q.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
                q.status === 'open' ? 'border-red-200' :
                q.status === 'resolved' ? 'border-green-200' : 'border-gray-100'}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : q.id)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[q.status]}`}>
                          {q.status === 'open' ? '⏳ Pending' : q.status === 'resolved' ? '✅ Resolved' : q.status === 'in_review' ? '👀 In Review' : '🔒 Closed'}
                        </span>
                        <span className="text-xs text-gray-400">{TYPE_LABELS[q.query_type]}</span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{q.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {q.project_name}{q.dept_name ? ` · ${q.dept_name}` : ''}
                        {q.proto_code ? ` · ${q.proto_code}` : ''}
                        <span className="ml-2">{formatTime(q.created_at)}</span>
                      </p>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-500 mb-1">My question:</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.description}</p>
                    </div>
                    {q.images?.length > 0 && (
                      <div className="bg-white border border-gray-100 rounded-xl p-3">
                        <p className="text-xs text-gray-500 mb-1">Attached images:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {q.images.map(img => (
                            <img key={img.id} src={img.image_url} alt="query" className="w-full h-16 object-cover rounded-xl" />
                          ))}
                        </div>
                      </div>
                    )}
                    {q.admin_response ? (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <p className="text-xs text-green-600 font-medium mb-1">✅ Admin ka jawab:</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.admin_response}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(q.responded_at)}</p>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                        <p className="text-xs text-yellow-700">⏳ Awaiting admin response — please wait</p>
                      </div>
                    )}

                    {/* Follow-up / Add more info — only for open queries */}
                    {(q.status === 'open' || q.status === 'in_review') && (
                      <div>
                        {followUpId !== q.id ? (
                          <button onClick={() => { setFollowUpId(q.id); setFollowUpText(''); }}
                            className="w-full border border-dashed border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 py-1.5 rounded-xl text-xs transition-colors">
                            + Add more info / Send follow-up
                          </button>
                        ) : (
                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
                            <p className="text-xs font-medium text-gray-600">📝 Additional Information:</p>
                            <textarea value={followUpText} onChange={e => setFollowUpText(e.target.value)}
                              rows={3} placeholder="What else would you like to tell the admin..."
                              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white resize-none" />
                            <div className="flex gap-2">
                              <button onClick={() => { setFollowUpId(null); setFollowUpText(''); }}
                                className="flex-1 border border-gray-200 text-gray-500 py-1.5 rounded-lg text-xs hover:bg-gray-100">Cancel</button>
                              <button onClick={() => handleFollowUp(q)} disabled={sendingFollowUp}
                                className="flex-1 bg-amber-500 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-amber-600 disabled:opacity-50">
                                {sendingFollowUp ? 'Sending...' : '📤 Send'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New Query Form Modal */}
      {showForm && (
        <div className="modal-backdrop">
          <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-3xl">
              <h2 className="font-bold text-lg text-gray-900">Raise New Query</h2>
              <button onClick={() => setShowForm(false)} className="modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div>
                <label className="field-label">Query Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(TYPE_LABELS).map(([v,l]) => (
                    <button key={v} onClick={() => setForm({...form, query_type: v})}
                      className={`text-xs py-2 px-3 rounded-xl border text-left transition-all ${
                        form.query_type === v ? 'border-amber-400 bg-amber-50 text-amber-800 font-medium' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Priority</label>
                <div className="flex gap-2">
                  {[['low','Low'],['medium','Medium'],['high','High'],['urgent','🚨 Urgent']].map(([v,l]) => (
                    <button key={v} onClick={() => setForm({...form, priority: v})}
                      className={`flex-1 text-xs py-2 rounded-xl border transition-all ${
                        form.priority === v ?
                          (v==='urgent'||v==='high' ? 'border-red-400 bg-red-50 text-red-700 font-bold' : 'border-amber-400 bg-amber-50 text-amber-700 font-bold')
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {myTasks.length > 0 && (
                <div>
                  <label className="field-label">Which Task? (optional)</label>
                  <select value={form.task_id} onChange={e => setForm({...form, task_id: e.target.value})}
                    className="field-input">
                    <option value="">Select Task (optional)</option>
                    {myTasks.map(t => <option key={t.id} value={t.id}>{t.task_title} — {t.project_name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="field-label">Project *</label>
                <ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})}
              placeholder="Select Project" className="field-input" />
              </div>

              {items.length > 0 && (
                <div>
                  <label className="field-label">Which Item?</label>
                  <select value={form.project_item_id} onChange={e => setForm({...form, project_item_id: e.target.value})}
                    className="field-input">
                    <option value="">Select Item (optional)</option>
                    {items.map(i => <option key={i.id} value={i.id}>{i.proto_code} — {i.item_name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="field-label">Department</label>
                <select value={form.department_id} onChange={e => setForm({...form, department_id: e.target.value})}
                  className="field-input">
                  <option value="">Select Dept (optional)</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div>
                <label className="field-label">Title (Short) *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="e.g. Machine stopped working, Material finished"
                  className="field-input" />
              </div>

              <div>
                <label className="field-label">Describe in detail *</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  rows={4} placeholder="Describe in detail — what happened, when, what is needed..."
                  className="field-input" />
              </div>

              {form.query_type === 'material_request' && (
                <div>
                  <label className="field-label">MR Images (optional)</label>
                  <input type="file" multiple accept="image/*" onChange={handleFilesChange}
                    className="w-full text-xs text-gray-600" />
                  {form.images?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      {form.images.length} file(s) selected.
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-5 gap-2">
                    {form.images?.map((file, idx) => (
                      <div key={idx} className="text-center text-[10px] text-gray-400 truncate">
                        {file.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pb-2">
                <button onClick={() => setShowForm(false)} className="flex-1 border border-gray-200 rounded-xl py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={handleSubmit} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" /> Submit Query
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
